import { ProjectRouteParams } from "@app/projects/projectRouteParams";
import { Component } from "react";
import { Route, RouteComponentProps, Switch } from "react-router-dom";
import { ProjectPerspective } from "@app/projects/enumerators";
import { CountyProjectContextProps, withCountyProjectContext } from "@app/projects/CountyProjectContext";
import { AdequacyAggregateDetails } from "@app/projects/network-assessment/adequacy/AdequacyAggregateDetails";
import { AdequacyDetails } from "@app/projects/network-assessment/adequacy/AdequacyDetails";
import AdequacyViewByAllContent from "@app/projects/network-assessment/adequacy/AdequacyViewByAll";
import RegionAggregateView from "@app/projects/network-assessment/adequacy/RegionAggregateView";
import DetailsFilters from "@app/projects/network-assessment/adequacy/DetailsFilters";
import { shouldSwitchProjectRunConfigurationsInCompareMode } from "@app/projects/helpers";

type DetailsProps = RouteComponentProps<unknown> & CountyProjectContextProps;

class Details extends Component<DetailsProps> {
  constructor(props: DetailsProps) {
    super(props);
  }

  public render() {
    let exportRunConfigurationId = this.props.runConfiguration ? this.props.runConfiguration.projectRunConfigurationId : null;
    let exportCompareToRunConfigurationId = this.props.compareToRunConfiguration ? this.props.compareToRunConfiguration.projectRunConfigurationId : null;
    if (this.props.compareToRunConfiguration && shouldSwitchProjectRunConfigurationsInCompareMode(this.props.runConfiguration, this.props.compareToRunConfiguration)) {
      /** If true, the compared run configuration providers should be displayed on top. */
      exportRunConfigurationId = this.props.compareToRunConfiguration.projectRunConfigurationId;
      exportCompareToRunConfigurationId = this.props.runConfiguration.projectRunConfigurationId;
    }

    const exportEndpoint = this.props.runConfiguration ? `${this.props.match.url}/export/${exportRunConfigurationId}/${this.props.runConfiguration.isLatestPublished}` : "";
    const mappingEndPoint = this.props.location?.pathname?.replace("adequacy", "mapping");
    const countyPerspectiveOnlyItems = this.props.perspective === ProjectPerspective.ByCounty;
    const showRegionStatusSelectors = this.props.perspective === ProjectPerspective.ByCounty
      || this.props.perspective === ProjectPerspective.All
      || this.props.perspective === ProjectPerspective.ByState
      || this.props.perspective === ProjectPerspective.ByRegion;

    return (
      <>
        {this.props.perspective !== ProjectPerspective.All && <DetailsFilters {...this.props} disabled={(this.props.loading || this.props.loadingResults) && !this.props.project.isReadOnly} showRegionStatusSelectors={showRegionStatusSelectors} />}
        <Switch>
          {this.props.perspective === ProjectPerspective.All && !this.props.regionFilters.county && !!this.props.runConfiguration && (
            <Route
              exact={false}
              path={this.props.match.url}
              render={() => (
                <AdequacyViewByAllContent
                  exportComparedRunConfigurationId={exportCompareToRunConfigurationId}
                  exportEndpoint={exportEndpoint}
                  location={this.props.location}
                  history={this.props.history}
                  match={this.props.match}
                />
              )}
            />
          )}

          {this.props.perspective === ProjectPerspective.ByRegion && (
            <Route
              exact={true}
              path={this.props.match.url}
              render={() => <RegionAggregateView exportComparedRunConfigurationId={exportCompareToRunConfigurationId} exportEndpoint={exportEndpoint} />}
            />
          )}

          {this.props.availablePerspectives.includes(ProjectPerspective.ByRegion) && (
            <Route
              exact={true}
              path={`${this.props.match.path}/region/:regionSetId`}
              render={(props: RouteComponentProps<{ [x: string]: string }, unknown, ProjectRouteParams>) => <AdequacyAggregateDetails exportComparedRunConfigurationId={exportCompareToRunConfigurationId} exportEndpoint={exportEndpoint} countyPerspectiveOnlyItems={true} {...props} />}
            />
          )}

          <Route
            exact={true}
            path={this.props.match.url}
            render={(props: RouteComponentProps<{ [x: string]: string }, unknown, ProjectRouteParams>) => <AdequacyAggregateDetails exportComparedRunConfigurationId={exportCompareToRunConfigurationId} exportEndpoint={exportEndpoint} countyPerspectiveOnlyItems={countyPerspectiveOnlyItems} {...props} />}
          />

          <Route
            exact={true}
            path={`${this.props.match.path}/specialty/:specialtyGroupCode`}
            render={(props: RouteComponentProps<{ [x: string]: string }, unknown, ProjectRouteParams>) => <AdequacyAggregateDetails exportComparedRunConfigurationId={exportCompareToRunConfigurationId} exportEndpoint={exportEndpoint} countyPerspectiveOnlyItems={countyPerspectiveOnlyItems} {...props} />}
          />
          <Route
            exact={true}
            path={`${this.props.match.path}/specialty/:specialtyGroupCode/state/:stateRegionCode`}
            render={(props: RouteComponentProps<{ [x: string]: string }, unknown, ProjectRouteParams>) => <AdequacyDetails exportComparedRunConfigurationId={exportCompareToRunConfigurationId} exportEndpoint={exportEndpoint}{...props} mappingEndpoint={mappingEndPoint} />}
          />
          <Route
            exact={true}
            path={`${this.props.match.path}/specialty/:specialtyGroupCode/county/:countyRegionCode`}
            render={(props: RouteComponentProps<{ [x: string]: string }, unknown, ProjectRouteParams>) => <AdequacyDetails exportComparedRunConfigurationId={exportCompareToRunConfigurationId} exportEndpoint={exportEndpoint}{...props} mappingEndpoint={mappingEndPoint} />}
          />
          <Route
            exact={true}
            path={`${this.props.match.path}/specialty/:specialtyGroupCode/state/:stateRegionCode/county/:countyRegionCode`}
            render={(props: RouteComponentProps<{ [x: string]: string }, unknown, ProjectRouteParams>) => <AdequacyDetails exportComparedRunConfigurationId={exportCompareToRunConfigurationId} exportEndpoint={exportEndpoint}{...props} mappingEndpoint={mappingEndPoint} />}
          />

          <Route
            exact={true}
            path={`${this.props.match.path}/state/:stateRegionCode`}
            render={(props: RouteComponentProps<{ [x: string]: string }, unknown, ProjectRouteParams>) => <AdequacyAggregateDetails exportComparedRunConfigurationId={exportCompareToRunConfigurationId} exportEndpoint={exportEndpoint} countyPerspectiveOnlyItems={countyPerspectiveOnlyItems} {...props} />}
          />
          <Route
            exact={true}
            path={`${this.props.match.path}/state/:stateRegionCode/county/:countyRegionCode`}
            render={(props: RouteComponentProps<{ [x: string]: string }, unknown, ProjectRouteParams>) => <AdequacyDetails exportComparedRunConfigurationId={exportCompareToRunConfigurationId} exportEndpoint={exportEndpoint}{...props} mappingEndpoint={mappingEndPoint} />}
          />
          <Route
            exact={true}
            path={`${this.props.match.path}/state/:stateRegionCode/specialty/:specialtyGroupCode`}
            render={(props: RouteComponentProps<{ [x: string]: string }, unknown, ProjectRouteParams>) => <AdequacyDetails exportComparedRunConfigurationId={exportCompareToRunConfigurationId} exportEndpoint={exportEndpoint}{...props} mappingEndpoint={mappingEndPoint} />}
          />
          <Route
            exact={true}
            path={`${this.props.match.path}/state/:stateRegionCode/county/:countyRegionCode/specialty/:specialtyGroupCode`}
            render={(props: RouteComponentProps<{ [x: string]: string }, unknown, ProjectRouteParams>) => <AdequacyDetails exportComparedRunConfigurationId={exportCompareToRunConfigurationId} exportEndpoint={exportEndpoint}{...props} mappingEndpoint={mappingEndPoint} />}
          />
          <Route
            exact={true}
            path={`${this.props.match.path}/state/:stateRegionCode/specialty/:specialtyGroupCode/county/:countyRegionCode`}
            render={(props: RouteComponentProps<{ [x: string]: string }, unknown, ProjectRouteParams>) => <AdequacyDetails exportComparedRunConfigurationId={exportCompareToRunConfigurationId} exportEndpoint={exportEndpoint}{...props} mappingEndpoint={mappingEndPoint} />}
          />

          <Route
            exact={true}
            path={`${this.props.match.path}/county/:countyRegionCode/specialty/:specialtyGroupCode`}
            render={(props: RouteComponentProps<{ [x: string]: string }, unknown, ProjectRouteParams>) => <AdequacyDetails exportComparedRunConfigurationId={exportCompareToRunConfigurationId} exportEndpoint={exportEndpoint}{...props} mappingEndpoint={mappingEndPoint} />}
          />
          <Route
            exact={true}
            path={`${this.props.match.path}/county/:countyRegionCode`}
            render={(props: RouteComponentProps<{ [x: string]: string }, unknown, ProjectRouteParams>) => <AdequacyDetails exportComparedRunConfigurationId={exportCompareToRunConfigurationId} exportEndpoint={exportEndpoint}{...props} mappingEndpoint={mappingEndPoint} />}
          />
        </Switch>
      </>
    );
  }
}

const WrappedDetails = withCountyProjectContext(Details);

export { WrappedDetails as Details };
